import * as R from 'Router/routes';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MODAL_ENABLED_ROUTES = [
  R.LOAN_REJECTED,
  R.LOAN_FAILED,
  R.REJECTED_TIMER,
  R.REJECTED_TIMER_FLOW,
];
const MODAL_SHOW_DELAY_MS = 1000;
const MODAL_TIMEOUT_MS = 5000;
const MODAL_ID = 're';

export const useGTMModal = () => {
  const { pathname } = useLocation();
  const [modalDelayFinished, setModalDelayFinished] = useState(false);

  const shouldShowModal = MODAL_ENABLED_ROUTES.includes(pathname);
  const existingModalScript = document.getElementById('gtm_modal');

  const modalRef = useRef<HTMLElement | null>(null);

  // Inject GTM modal + reset/apply delay
  useEffect(() => {
    if (!existingModalScript) createAndAppendModalScript();

    // Apply 1s delay to show the modal or reset delay
    if (shouldShowModal) {
      setTimeout(() => {
        setModalDelayFinished(true);
      }, MODAL_SHOW_DELAY_MS);
    } else {
      setModalDelayFinished(false);
    }
  }, [pathname]);

  // Show hide modal
  useEffect(() => {
    const showHideModal = async () => {
      try {
        // Try to get the modal
        if (!modalRef.current) modalRef.current = await waitForModal();
      } catch (error) {
        console.error('Failed to handle GTM modal:', error);
      }

      // update the hidden property if modalRef is defined
      if (modalRef.current) {
        if (shouldShowModal && modalDelayFinished)
          modalRef.current.hidden = false;
        else modalRef.current.hidden = true;
      }
    };
    showHideModal();
  }, [shouldShowModal, modalDelayFinished]);

  // Function to wait until script injects the modal
  const waitForModal = (
    timeoutMs = MODAL_TIMEOUT_MS,
  ): Promise<HTMLElement | null> | HTMLElement => {
    const existingModal = document.getElementById(MODAL_ID);

    if (existingModal) return existingModal;

    return new Promise(
      (resolve: (value: HTMLElement | null) => void, reject) => {
        const modalObserver = new MutationObserver(() => {
          const modal: HTMLElement | null = document.getElementById(MODAL_ID);

          if (modal) {
            modalObserver.disconnect();
            resolve(modal);
          }
        });

        modalObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });

        setTimeout(() => {
          modalObserver.disconnect();
          reject(
            new Error('GTM modal did not appear within expected timeframe'),
          );
        }, timeoutMs);
      },
    );
  };

  // Inject script function
  const createAndAppendModalScript = () => {
    const modalScript = document.createElement('script');
    modalScript.type = 'text/javascript';
    modalScript.id = 'gtm_modal';
    modalScript.async = true;
    modalScript.src = 'https://dev.4finance.com/modal.js';
    document.body.appendChild(modalScript);
  };
};
